import { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle, Crown, Flame, Star, Shield, Gem, Zap, Circle,
  ArrowUpRight, ArrowDownLeft, RefreshCw, AlertTriangle,
  CalendarClock, CreditCard, BadgeCheck, XCircle,
} from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/guest.png";
import { UserDataContext } from "../context/User";

// ── Tier config ────────────────────────────────────────────────────────────────
const TIER_ORDER = ["free","wanderer","kindred","quester","pilgrim","guardian","spire"];

const TIER_CONFIG = {
  free:     { gradient: "from-gray-400 to-gray-500",       Icon: Circle,  ring: "ring-gray-300"   },
  wanderer: { gradient: "from-cyan-400 to-blue-500",        Icon: Zap,     ring: "ring-cyan-400"   },
  kindred:  { gradient: "from-purple-400 to-purple-600",    Icon: Flame,   ring: "ring-purple-400" },
  quester:  { gradient: "from-amber-400 to-orange-500",     Icon: Star,    ring: "ring-amber-400"  },
  pilgrim:  { gradient: "from-emerald-400 to-teal-600",     Icon: Shield,  ring: "ring-emerald-400"},
  guardian: { gradient: "from-rose-400 to-red-600",         Icon: Gem,     ring: "ring-rose-400"   },
  spire:    { gradient: "from-yellow-400 to-amber-600",     Icon: Crown,   ring: "ring-yellow-400" },
};

const STATUS_CONFIG = {
  active:    { label: "Active",    color: "bg-green-100 text-green-700 border-green-200",  Icon: CheckCircle  },
  trialing:  { label: "Trialing",  color: "bg-blue-100 text-blue-700 border-blue-200",     Icon: RefreshCw    },
  past_due:  { label: "Past Due",  color: "bg-amber-100 text-amber-700 border-amber-200",  Icon: AlertTriangle},
  canceled:  { label: "Canceled",  color: "bg-red-100 text-red-700 border-red-200",        Icon: XCircle      },
  incomplete:{ label: "Incomplete",color: "bg-gray-100 text-gray-600 border-gray-200",     Icon: AlertTriangle},
};

const getTierRank = (key) => {
  const r = TIER_ORDER.indexOf((key || "free").toLowerCase());
  return r === -1 ? 0 : r;
};

// ── Main Component ─────────────────────────────────────────────────────────────
const Membership = () => {
  const navigate = useNavigate();
  const { userData, fetchUser } = useContext(UserDataContext);

  const [tiers, setTiers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [upgrading, setUpgrading]   = useState(null); // tier key being processed
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [message, setMessage]       = useState(null); // { type, text }

  const currentTierKey = (userData?.membershipTier?.key || userData?.primaryRoleKey || "free").toLowerCase();
  const currentRank    = getTierRank(currentTierKey);
  const subStatus      = userData?.subscription?.status;
  const periodEnd      = userData?.subscription?.renewsAt || userData?.subscription?.currentPeriodEnd;

  // Fetch tiers
  const fetchTiers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch("/api/membership/fetch");
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        const HIDDEN_KEYS = ["visitor", "admin", "guest"];
        const sorted = data.data
          .filter(
            (t) =>
              t.isActive !== false &&
              !HIDDEN_KEYS.includes(t.key?.toLowerCase())
          )
          .sort((a, b) => getTierRank(a.key) - getTierRank(b.key));
        setTiers(sorted);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  // Handle upgrade / downgrade
  const handlePlanAction = async (tier) => {
    if (!userData) {
      navigate("/register", {
        state: {
          id: tier._id, key: tier.key, title: tier.title,
          points: tier.points, monthlyPrice: tier.monthlyPrice,
          yearlyPrice: tier.yearlyPrice,
        },
      });
      return;
    }

    if (tier.key.toLowerCase() === currentTierKey) return;

    setUpgrading(tier.key);
    setMessage(null);

    try {
      const res = await axios.put(
        "/api/subscriptions/change-subscription",
        { membershipTierKey: tier.key, billingCycle },
        { withCredentials: true }
      );

      if (res.data.success) {
        if (res.data.data?.checkoutUrl) {
          window.location.href = res.data.data.checkoutUrl;
          return;
        }
        await fetchUser();
        setMessage({ type: "success", text: res.data.message || `Successfully switched to ${tier.title}.` });
      } else {
        setMessage({ type: "error", text: res.data.message || "Failed to change plan. Please try again." });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      setMessage({ type: "error", text: msg });
    } finally {
      setUpgrading(null);
    }
  };

  const getButtonLabel = (tier) => {
    if (!userData) return "Get Started";
    const tierKey = tier.key.toLowerCase();
    if (tierKey === currentTierKey) return "Current Plan";
    if (getTierRank(tierKey) > currentRank) return "Upgrade";
    return "Downgrade";
  };

  const getButtonStyle = (tier) => {
    const tierKey = tier.key.toLowerCase();
    if (tierKey === currentTierKey)
      return "bg-gray-100 text-gray-400 cursor-default border border-gray-200";
    if (getTierRank(tierKey) > currentRank)
      return "bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black hover:opacity-90 hover:scale-105 shadow-md";
    return "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50";
  };

  const tc = (key) => TIER_CONFIG[(key || "free").toLowerCase()] || TIER_CONFIG.free;

  return (
    <div className="App">
      <Header isBlack={false} />
      <Banner
        backgroundImage={BannerImg}
        heading="Membership Plans"
        paragraph="Choose the plan that matches your creative journey. Upgrade or downgrade anytime."
        bton="Browse Plans"
        minHeight="60vh"
        padding="80px 0"
      />

      <section className="bg-[#F9FAFB] py-16 px-4">
        <div className="max-w-6xl mx-auto">

          {/* ── Current Plan Status (logged-in only) ─────────────── */}
          {userData && (
            <div className="mb-12">
              <h3 className="font-cinzel text-2xl font-bold text-gray-900 mb-4">
                Your Membership
              </h3>

              <div className={`bg-white rounded-3xl border-2 ${tc(currentTierKey).ring} ring-2 shadow-lg p-6`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">

                  {/* Tier icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tc(currentTierKey).gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                    {(() => { const { Icon } = tc(currentTierKey); return <Icon size={30} className="text-white" strokeWidth={1.5} />; })()}
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h4 className="font-cinzel text-xl font-bold text-gray-900 capitalize">
                        {currentTierKey} Plan
                      </h4>
                      {subStatus && STATUS_CONFIG[subStatus] && (() => {
                        const sc = STATUS_CONFIG[subStatus];
                        const SIcon = sc.Icon;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border font-outfit ${sc.color}`}>
                            <SIcon size={11} strokeWidth={2.5} />
                            {sc.label}
                          </span>
                        );
                      })()}
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm font-outfit text-gray-500">
                      {userData?.memberId && (
                        <span className="flex items-center gap-1.5">
                          <CreditCard size={13} className="text-gray-400" />
                          {userData.memberId}
                        </span>
                      )}
                      {periodEnd && (
                        <span className="flex items-center gap-1.5">
                          <CalendarClock size={13} className="text-gray-400" />
                          Renews {new Date(periodEnd).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                        </span>
                      )}
                      {userData?.growthSparks > 0 && (
                        <span className="flex items-center gap-1.5">
                          <Zap size={13} className="text-amber-400" />
                          {userData.growthSparks} Growth Sparks
                        </span>
                      )}
                      {userData?.badges?.length > 0 && (
                        <span className="flex items-center gap-1.5">
                          <BadgeCheck size={13} className="text-cyan-400" />
                          {userData.badges.length} Badge{userData.badges.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Manage button */}
                  {subStatus && subStatus !== "canceled" && currentTierKey !== "free" && (
                    <button
                      onClick={() => navigate("/profile")}
                      className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-300 text-gray-600 font-outfit text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      <RefreshCw size={14} />
                      Manage
                    </button>
                  )}
                </div>

                {/* Past due / inactive warning */}
                {(subStatus === "past_due" || subStatus === "incomplete") && (
                  <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-2">
                    <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />
                    <p className="font-outfit text-sm text-amber-700">
                      Your payment is overdue. Please update your payment method to keep your access.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Flash message ─────────────────────────────────────── */}
          {message && (
            <div className={`mb-8 p-4 rounded-2xl flex items-center gap-3 font-outfit text-sm
              ${message.type === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
              }`}>
              {message.type === "success"
                ? <CheckCircle size={16} className="flex-shrink-0" />
                : <AlertTriangle size={16} className="flex-shrink-0" />
              }
              <span className="flex-1">{message.text}</span>
              <button
                onClick={() => setMessage(null)}
                className="ml-auto opacity-60 hover:opacity-100 text-current font-bold text-base leading-none"
              >
                ✕
              </button>
            </div>
          )}

          {/* ── Billing toggle ────────────────────────────────────── */}
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div>
              <h3 className="font-cinzel text-2xl font-bold text-gray-900">
                {userData ? "Change Your Plan" : "Choose Your Plan"}
              </h3>
              <p className="font-outfit text-gray-500 text-sm mt-1">
                {userData ? "Upgrade or downgrade anytime. Changes take effect immediately." : "Join the LWBL community today."}
              </p>
            </div>

            {/* Billing cycle toggle */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-4 py-1.5 rounded-full text-sm font-outfit font-medium transition-all duration-200
                  ${billingCycle === "monthly" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-4 py-1.5 rounded-full text-sm font-outfit font-medium transition-all duration-200
                  ${billingCycle === "yearly" ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
              >
                Yearly
                <span className="ml-1.5 text-[10px] font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">
                  Save
                </span>
              </button>
            </div>
          </div>

          {/* ── Tier Cards ────────────────────────────────────────── */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : tiers.length === 0 ? (
            <p className="text-center font-outfit text-gray-400 py-20">No membership plans available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {tiers.map((tier) => {
                const key       = tier.key.toLowerCase();
                const config    = tc(key);
                const { Icon }  = config;
                const isCurrent = key === currentTierKey;
                const isUpgrade = getTierRank(key) > currentRank;
                const price     = billingCycle === "yearly" ? tier.yearlyPrice : tier.monthlyPrice;
                const isLoading = upgrading === tier.key;

                return (
                  <div
                    key={tier._id}
                    className={`relative bg-white rounded-3xl border-2 transition-all duration-300 overflow-hidden
                      ${isCurrent
                        ? `${config.ring} ring-2 shadow-xl scale-[1.02]`
                        : "border-gray-100 hover:border-gray-200 hover:shadow-lg hover:-translate-y-1"
                      }`}
                  >
                    {/* Current plan ribbon */}
                    {isCurrent && (
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${config.gradient}`} />
                    )}

                    {/* Popular badge */}
                    {key === "wanderer" && !isCurrent && (
                      <div className="absolute top-4 right-4">
                        <span className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white text-[10px] font-bold font-outfit px-2.5 py-1 rounded-full">
                          POPULAR
                        </span>
                      </div>
                    )}

                    <div className="p-6">
                      {/* Icon + title */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-sm`}>
                          <Icon size={20} className="text-white" strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 text-left">
                          <h3 className="font-cinzel font-bold text-gray-900 text-lg leading-tight">
                            {tier.title}
                          </h3>
                          {isCurrent && (
                            <span className="text-xs font-outfit text-gray-400">Your current plan</span>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mb-5">
                        {price > 0 ? (
                          <div className="flex items-end gap-1">
                            <span className="font-cinzel text-3xl font-bold text-gray-900">
                              ${price}
                            </span>
                            <span className="font-outfit text-gray-400 text-sm mb-1">
                              /{billingCycle === "yearly" ? "yr" : "mo"}
                            </span>
                          </div>
                        ) : (
                          <span className="font-cinzel text-3xl font-bold text-gray-900">Free</span>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-2.5 mb-6">
                        {tier.points.map((point, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <CheckCircle
                              size={15}
                              className={`flex-shrink-0 mt-0.5 ${isCurrent ? "text-cyan-500" : "text-gray-400"}`}
                              strokeWidth={2.5}
                            />
                            <span className="font-outfit text-sm text-gray-600 leading-snug text-left">{point}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <button
                        onClick={() => !isCurrent && handlePlanAction(tier)}
                        disabled={isCurrent || isLoading}
                        className={`w-full py-3 rounded-2xl font-outfit font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2
                          ${getButtonStyle(tier)}`}
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        ) : isCurrent ? (
                          <>
                            <CheckCircle size={15} strokeWidth={2.5} />
                            Current Plan
                          </>
                        ) : isUpgrade ? (
                          <>
                            <ArrowUpRight size={15} strokeWidth={2.5} />
                            Upgrade to {tier.title.split(" ")[0]}
                          </>
                        ) : (
                          <>
                            <ArrowDownLeft size={15} strokeWidth={2.5} />
                            {userData ? "Downgrade" : "Get Started"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── FAQ / Note ────────────────────────────────────────── */}
          <div className="mt-12 p-6 bg-white rounded-3xl border border-gray-100 shadow-sm">
            <h4 className="font-cinzel font-bold text-gray-900 text-lg mb-4">Membership Notes</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm font-outfit text-gray-500">
              <div className="flex items-start gap-2.5">
                <RefreshCw size={15} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                <span>Upgrade or downgrade anytime. Changes take effect immediately.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <CreditCard size={15} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                <span>Payments are processed securely via Stripe. Cancel anytime.</span>
              </div>
              <div className="flex items-start gap-2.5">
                <Shield size={15} className="text-cyan-500 flex-shrink-0 mt-0.5" />
                <span>Your data and creative work are always protected and private.</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Membership;
